import { analyticsAllowed, privacySubjectId } from "./privacy-choices";

/**
 * First-party product events. Nothing here is sent unless analytics consent
 * is stored for the current policy version. Payloads are an allowlist:
 * page, signup, onboarding step, Pro upgrade, or feature name.
 */

export const PAGE_PATHS = [
  "/",
  "/sign-in",
  "/sign-up",
  "/onboarding",
  "/dashboard",
  "/plate-log",
  "/plate-talk",
  "/grocery",
  "/progress-photos",
  "/wearables",
  "/integrations",
  "/oura",
  "/genetics",
  "/subscription",
  "/help",
  "/account",
  "/feature-tour",
  "/foods",
  "/metrics",
  "/settings/privacy",
] as const;

export const FEATURE_BY_PATH: Record<string, "dashboard" | "plate_log" | "plate_talk" | "grocery" | "progress" | "wearables" | "genetics_overview" | "subscription"> = {
  "/dashboard": "dashboard",
  "/plate-log": "plate_log",
  "/plate-talk": "plate_talk",
  "/grocery": "grocery",
  "/progress-photos": "progress",
  "/wearables": "wearables",
  "/integrations": "wearables",
  "/oura": "wearables",
  "/genetics": "genetics_overview",
  "/subscription": "subscription",
};

export type ProductEvent =
  | { name: "page_view"; path: (typeof PAGE_PATHS)[number] }
  | { name: "signup" }
  | { name: "onboarding_step"; step: "language" | "profile" | "body" | "goals" }
  | { name: "upgrade_pro" }
  | { name: "feature_use"; feature: (typeof FEATURE_BY_PATH)[string] };

const oncePerPageLoad = new Set<string>();

export function eventsForLocation(location: string): ProductEvent[] {
  const path = location.split("?")[0]?.split("#")[0] ?? "";
  if (!PAGE_PATHS.includes(path as (typeof PAGE_PATHS)[number])) return [];
  const events: ProductEvent[] = [{ name: "page_view", path: path as (typeof PAGE_PATHS)[number] }];
  const feature = FEATURE_BY_PATH[path];
  if (feature) events.push({ name: "feature_use", feature });
  return events;
}

export async function recordProductEvent(event: ProductEvent): Promise<void> {
  try {
    if (!analyticsAllowed()) return;
    if (event.name === "signup" || event.name === "upgrade_pro" || event.name === "onboarding_step") {
      const key = JSON.stringify(event);
      if (oncePerPageLoad.has(key)) return;
      oncePerPageLoad.add(key);
    }
    const base = String(import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");
    await fetch(`${base}/api/privacy/events`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subjectId: privacySubjectId(), ...event }),
    });
  } catch {
    // Analytics must never interrupt the product.
  }
}
